import React, { Component } from "react";
import { withCookies, Cookies } from "react-cookie";
import { instanceOf } from "prop-types";
import { withRouter } from "react-router-dom";

import productsService from "@services/products";
import postosService from "@services/postos";
import apolloService from "@services/apollo";
import categoriesService from "@services/categories";

import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

// Components
import EmpresaSelect from "@components/EmpresaSelect";
import Menu from "@components/Menu";
import Input from "@components/Input";
import Switch from "@components/Switch";
import AutoComplete from "@components/AutoComplete";
import { NotificationManager } from "react-notifications";
import { MdEuro, MdAddCircleOutline } from "react-icons/md";
import { FaBoxes, FaEdit, FaClipboardList } from "react-icons/fa";

import { twoDecimal, threeDecimal, valueWithIva, valueWithoutIva } from "@utils/format.js";

import UserContext from "@components/context/userContext";

import "./list.css";

// Função para formatar valores com separadores de milhares
const milhares = (valor, currency = '€') => {
  // Verificar se o valor é null ou undefined
  if (valor === null || valor === undefined) {
    return "0,00 " + currency;
  }
  
  // Remove vírgulas se existirem (formato americano: 4,922.05)
  valor = valor.toString().replace(/,/g, '');
  valor = parseFloat(valor);
  
  if (isNaN(valor)) {
    return "0,00 " + currency;
  }
  
  var numeroFormatado = valor.toFixed(2);
  
  var partes = numeroFormatado.split('.');
  var parteInteira = partes[0];
  var parteDecimal = partes[1];
  
  var parteInteiraFormatada = parteInteira.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  return parteInteiraFormatada + ',' + parteDecimal + ' ' + currency;
};

// Função para formatar números com separadores de milhares (sem moeda)
const milharesNumber = (valor, decimals = 3) => {
  // Verificar se o valor é null ou undefined
  if (valor === null || valor === undefined) {
    return '';
  }
  
  // Remove vírgulas se existirem (formato americano: 4,922.05)
  valor = valor.toString().replace(/,/g, '');
  valor = parseFloat(valor);
  
  if (isNaN(valor)) {
    return '';
  }
  
  // Arredondar para o número de decimais especificado
  var numeroArredondado = valor;
  if (decimals > 0) {
    const factor = Math.pow(10, decimals);
    numeroArredondado = Math.round(valor * factor) / factor;
  } else {
    numeroArredondado = Math.round(valor);
  }
  
  // Converter para string e depois para número para remover zeros à direita
  var numeroFormatado = numeroArredondado.toFixed(decimals);
  
  var partes = numeroFormatado.split('.');
  var parteInteira = partes[0];
  var parteDecimal = partes[1];
  
  // Remover zeros à direita da parte decimal
  if (decimals > 0) {
    parteDecimal = parteDecimal.replace(/0+$/, '');
  }
  
  var parteInteiraFormatada = parteInteira.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  // Só adicionar vírgula e decimais se houver parte decimal significativa
  if (decimals > 0 && parteDecimal && parteDecimal.length > 0) {
    return parteInteiraFormatada + ',' + parteDecimal;
  }
  return parteInteiraFormatada;
};

// Função para converter valores já formatados com ponto decimal para formato PT (vírgula)
// Aceita strings que podem conter intervalos com setas
const formatPT = (valueStr) => {
  if (!valueStr) return '';
  
  const str = String(valueStr).trim();
  
  // Se contém intervalo (setas), formatar cada parte
  if (str.includes(' ↓↑ ')) {
    const parts = str.split(' ↓↑ ');
    return parts.map(part => formatSingleValuePT(part)).join(' ↓↑ ');
  }
  
  // Caso contrário, formatar o valor único
  return formatSingleValuePT(str);
};

// Função auxiliar para formatar um único valor
const formatSingleValuePT = (valueStr) => {
  if (!valueStr || valueStr === '-') return valueStr;
  
  const str = String(valueStr).trim();
  
  // Converter ponto para vírgula e adicionar separadores de milhares
  // Dividir em parte inteira e decimal
  const partes = str.split('.');
  let parteInteira = partes[0];
  const parteDecimal = partes[1] || '';
  
  // Adicionar separadores de milhares (espaços)
  parteInteira = parteInteira.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  // Retornar com vírgula se houver parte decimal
  if (parteDecimal) {
    return parteInteira + ',' + parteDecimal;
  }
  return parteInteira;
};

// Função para converter cor RGB ou hex para valores RGB
const parseColor = (color) => {
  if (!color) return null;

  const trimmed = color.trim();

  // Verificar se é formato RGB: rgb(r, g, b)
  const rgbMatch = trimmed.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10)
    };
  }

  // Verificar se é formato hex: #RRGGBB ou RRGGBB
  let hex = trimmed.replace('#', '');
  if (hex.length === 6) {
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
      return { r, g, b };
    }
  }

  return null;
};

// Função para calcular a luminosidade de uma cor e determinar se deve usar texto branco ou preto
const getContrastColor = (backgroundColor) => {
  if (!backgroundColor) {
    return '#000000'; // Fallback para preto se não houver cor
  }

  try {
    const rgb = parseColor(backgroundColor);
    
    if (!rgb || isNaN(rgb.r) || isNaN(rgb.g) || isNaN(rgb.b)) {
      return '#ffffff'; // Fallback para branco se formato inválido (assumir fundo escuro)
    }

    // Calcular luminosidade relativa usando a fórmula WCAG melhorada
    // Normalizar valores RGB para 0-1
    const rsRGB = rgb.r / 255;
    const gsRGB = rgb.g / 255;
    const bsRGB = rgb.b / 255;

    // Aplicar correção gamma
    const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

    // Calcular luminosidade relativa
    const luminance = 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;

    // Threshold de 0.5 - cores claras (> 0.5) usam texto preto, cores escuras (<= 0.5) usam texto branco
    return luminance > 0.5 ? '#000000' : '#ffffff';
  } catch (error) {
    return '#ffffff'; // Fallback para branco em caso de erro (assumir fundo escuro)
  }
};

// Função para converter cor RGB ou hex para formato válido CSS
const normalizeColor = (color) => {
  if (!color) return '#007bff'; // Cor padrão
  
  const trimmed = color.trim();
  
  // Se já é formato RGB válido, retornar como está
  if (trimmed.match(/rgb\s*\(/i)) {
    return trimmed;
  }
  
  // Se é formato hex, garantir que tem #
  if (trimmed.match(/^[0-9A-Fa-f]{6}$/)) {
    return `#${trimmed}`;
  }
  
  if (trimmed.match(/^#[0-9A-Fa-f]{6}$/)) {
    return trimmed;
  }
  
  return '#007bff'; // Fallback
};

// Função helper para extrair valores de um intervalo (suporta vários formatos: " ↓↑ ", " ↓ ... ↑ ", " ↑ ", " | ", " - ")
const parseInterval = (valueStr) => {
  if (!valueStr) return { min: null, max: null };
  const str = String(valueStr).trim();
  
  // Tentar primeiro o novo formato com setas juntas, depois outros para compatibilidade
  if (str.includes(' ↓↑ ')) {
    // Formato: "min ↓↑ max"
    const parts = str.split(' ↓↑ ');
    return {
      min: parts[0] && parts[0].trim() !== '' ? parseFloat(parts[0].trim().replace(/,/g, '.')) : null,
      max: parts[parts.length - 1] && parts[parts.length - 1].trim() !== '' ? parseFloat(parts[parts.length - 1].trim().replace(/,/g, '.')) : null
    };
  } else if (str.includes(' ↓ ') && str.includes(' ↑ ')) {
    // Formato: "↓ min ↑ max"
    const parts = str.split(' ↑ ');
    if (parts.length === 2) {
      const minPart = parts[0].replace('↓', '').trim();
      const maxPart = parts[1].trim();
      return {
        min: minPart !== '' ? parseFloat(minPart.replace(/,/g, '.')) : null,
        max: maxPart !== '' ? parseFloat(maxPart.replace(/,/g, '.')) : null
      };
    }
  } else if (str.includes(' ↑ ')) {
    // Formato: "min ↑ max"
    const parts = str.split(' ↑ ');
    return {
      min: parts[0] && parts[0].trim() !== '' ? parseFloat(parts[0].trim().replace(/,/g, '.')) : null,
      max: parts[parts.length - 1] && parts[parts.length - 1].trim() !== '' ? parseFloat(parts[parts.length - 1].trim().replace(/,/g, '.')) : null
    };
  } else if (str.includes(' | ')) {
    const parts = str.split(' | ');
    return {
      min: parts[0] && parts[0].trim() !== '' ? parseFloat(parts[0].trim().replace(/,/g, '.')) : null,
      max: parts[parts.length - 1] && parts[parts.length - 1].trim() !== '' ? parseFloat(parts[parts.length - 1].trim().replace(/,/g, '.')) : null
    };
  } else if (str.includes(' - ')) {
    const parts = str.split(' - ');
    return {
      min: parts[0] && parts[0].trim() !== '' ? parseFloat(parts[0].trim().replace(/,/g, '.')) : null,
      max: parts[parts.length - 1] && parts[parts.length - 1].trim() !== '' ? parseFloat(parts[parts.length - 1].trim().replace(/,/g, '.')) : null
    };
  } else {
    const parsed = parseFloat(str.replace(/,/g, '.'));
    return {
      min: !isNaN(parsed) && isFinite(parsed) ? parsed : null,
      max: !isNaN(parsed) && isFinite(parsed) ? parsed : null
    };
  }
};

const avoidDuplication = (first, second, field) => {
  // Validar valores - não mostrar se são inválidos (null, undefined, NaN, Infinity)
  const isValid = (val) => val !== null && val !== undefined && !isNaN(val) && isFinite(val);
  
  const validFirst = isValid(first) ? first : null;
  const validSecond = isValid(second) ? second : null;
  
  // Se ambos são inválidos, retornar string vazia
  if (!validFirst && !validSecond) {
    return '';
  }
  
  // Se apenas um é válido, retornar só esse
  if (validFirst && !validSecond) {
    switch (field) {
      case 'iva':
        return validFirst;
      case 'profitper':
        return twoDecimal(validFirst);
      default:
        return threeDecimal(validFirst);
    }
  }
  
  if (!validFirst && validSecond) {
    switch (field) {
      case 'iva':
        return validSecond;
      case 'profitper':
        return twoDecimal(validSecond);
      default:
        return threeDecimal(validSecond);
    }
  }
  
  // Se ambos são válidos e iguais
  if (validFirst === validSecond) {
    switch (field) {
      case 'iva':
        return validFirst;
      case 'profitper':
        return twoDecimal(validFirst);
      default:
        return threeDecimal(validFirst);
    }
  }
  
  // Se ambos são válidos e diferentes, mostrar intervalo com setas juntas no meio
  switch (field) {
    case 'iva':
      return `${validFirst} ↓↑ ${validSecond}`;
    case 'profit':
    case 'profitper':
      return `${twoDecimal(validFirst)} ↓↑ ${twoDecimal(validSecond)}`;
    default:
      return `${threeDecimal(validFirst)} ↓↑ ${threeDecimal(validSecond)}`;
  }
}

const valueToShow = (
  minValues = {},
  maxValues = {},
  field,
  withIva,
  showIva
) => {
  if (field !== 'iva' && (parseFloat(minValues[field]) === 0 && parseFloat(maxValues[field]) === 0)) {
    return "";
  } else if (
    minValues[field] &&
    maxValues[field]
  ) {
    if (showIva) {
      if (withIva) {
        return avoidDuplication(minValues[field], maxValues[field], field);
      }
      return avoidDuplication(valueWithIva(minValues[field], minValues.iva), valueWithIva(maxValues[field], maxValues.iva), field);
    }
    if (withIva) {
      return avoidDuplication(valueWithoutIva(minValues[field], minValues.iva), valueWithoutIva(maxValues[field], maxValues.iva), field);
    }
    return avoidDuplication(minValues[field], maxValues[field], field);
  } else if (['profit', 'profitper'].includes(field)) {
    const minPrice = valueWithoutIva(minValues['price'], minValues.iva);
    const maxPrice = valueWithoutIva(maxValues['price'], maxValues.iva);
    const minCostPrice = minValues['costprice'];
    const maxCostPrice = maxValues['costprice'];
    if (field === 'profitper') {
      const minProfitPer = minCostPrice > 0 ? (100 - (minCostPrice * 100 / minPrice)) : 100;
      const maxProfitPer = maxCostPrice > 0 ? (100 - (maxCostPrice * 100 / maxPrice)) : 100;
      return avoidDuplication(minProfitPer, maxProfitPer, field);
    }
    const minProfit = minPrice - minCostPrice;
    const maxProfit = maxPrice - maxCostPrice;
    return avoidDuplication(minProfit, maxProfit, field);
  } else {
    return field !== 'iva' ? threeDecimal(minValues[field]) : minValues[field];
  }
};

const importantFlags = (flags) => {
  const result = {};

  const priority = {
    "ALERT": 2,  // Higher value means higher priority
    "WARN": 1
  };

  flags.forEach(flag => {
      const { key, value } = flag;
      if (!result[key] || (priority[value] > priority[result[key]])) {
          result[key] = value;
      }
  });

  return result;
}

class Page extends Component {
  static contextType = UserContext;

  static propTypes = {
    cookies: instanceOf(Cookies).isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      canLoadProducts: true,
      loaded: false,
      synced: false,
      invalidProducts: [],
      loadingInvalidProducts: false,
      invalidProductsShowLimit: 12,
      table: {
        columns: [
          {
            Header: "Produto",
            accessor: "name",
          },
          {
            Header: "P.C. S/IVA",
            accessor: "costprice",
          },
          {
            Header: "P.C. C/IVA",
            accessor: "costprice_iva",
          },
          {
            Header: "PVP s/IVA",
            accessor: "price",
          },
          {
            Header: "PVP c/IVA",
            accessor: "price_iva",
          },
          {
            Header: "Margem s/IVA",
            accessor: "profit",
          },
          {
            Header: "Margem (%)",
            accessor: "profitper",
          },
          {
            Header: "IVA (%)",
            accessor: "iva",
          },
          {
            Header: "Familia",
            accessor: "category",
          },
          {
            Header: 'Estado',
            id: 'inativo',
            accessor: d => {
              return (
                <table style={{width: '100%'}}>
                  <tr>
                    <td>
                      {d.inativo === 't' ? (
                        <span className="badge badge-danger">INATIVO</span>
                      ) : (
                        <span className="badge badge-success">ATIVO</span>
                      )}
                    </td>
                    <td>
                      {/* {Object.keys(d.flags).map((key) => {
                        if (key === 'LOW_STOCK') {
                          return d.flags[key].value === 'WARN' ? (
                            <span className="badge badge-warning">Stock baixo</span>
                          ) : (
                            <span className="badge badge-danger">Stock muito baixo</span>
                          )
                        } else {
                          return null;
                        }
                      })} */}
                    </td>
                  </tr>
                </table>
              );
            },
          },
          {
            Header: "",
            accessor: "edit",
          },
        ],
        rows: [],
        loading: true,
      },
      search: JSON.parse(localStorage.getItem("products.search")) || "",
      idcategory: JSON.parse(localStorage.getItem("products.idcategory")) || {
        value: "",
        label: "TODAS",
      },
      status: JSON.parse(localStorage.getItem("products.status")) || {
        value: "f",
        label: "ATIVOS",
      },
      allStatus: [
        { value: "", label: "TODOS" },
        { value: "f", label: "ATIVOS" },
        { value: "t", label: "INATIVOS" },
      ],
      sortBy: JSON.parse(localStorage.getItem("products.sortBy")) || {
        value: "name-asc",
        label: "Nome (A-Z)",
      },
      allSortBy: [
        { value: "name-asc", label: "Nome (A-Z)" },
        { value: "name-desc", label: "Nome (Z-A)" },
        { value: "price-desc", label: "Preço (maior)" },
        { value: "price-asc", label: "Preço (menor)" },
        { value: "profit-desc", label: "Lucratividade (maior)" },
        { value: "profit-asc", label: "Lucratividade (menor)" },
      ],
      showPostosModal: false,
      selectedPostos: [],
      warehouses: [],
      currentPage: 1,
      itemsPerPage: 10,
      categoriesMap: {},
      showImportModal: false,
      showMappingModal: false,
      csvColumns: [],
      csvData: [],
      csvSeparator: ';',
      columnMapping: {},
      selectedFile: null,
      codeConflicts: [],
      isDragging: false,
      autoMappedColumns: [],
      maxBarcodeIndex: 3, // Começar com 3 códigos de barras disponíveis
      availableFields: [
        { value: '', label: 'Ignorar coluna' },
        { value: 'codigo', label: 'Código' },
        { value: 'nome', label: 'Nome' },
        { value: 'familia', label: 'Família (ID)' },
        { value: 'nome_familia', label: 'Nome da Família' },
        { value: 'pvp', label: 'PVP' },
        { value: 'iva', label: 'IVA (%)' },
        { value: 'cbarras1', label: 'Código de Barras 1' },
        { value: 'cbarras2', label: 'Código de Barras 2' },
        { value: 'cbarras3', label: 'Código de Barras 3' },
        { value: 'precocusto', label: 'Preço de Custo' },
        { value: 'isp', label: 'ISP' },
        { value: 'inativo', label: 'Inativo' },
        { value: 'combustivel', label: 'Combustível' },
        { value: 'gas', label: 'Gás' },
        { value: 'composto', label: 'Composto' },
      ],
    };
    this.loadFunction = this.loadFunction.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.uploadFunction = this.uploadFunction.bind(this);
    this.getInvalidProducts = this.getInvalidProducts.bind(this);
    this.fileInputRef = React.createRef();
    this.handleImportClick = this.handleImportClick.bind(this);
    this.handleFileChange = this.handleFileChange.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handlePostosModalOpen = this.handlePostosModalOpen.bind(this);
    this.handlePostosModalClose = this.handlePostosModalClose.bind(this);
    this.handlePostosChange = this.handlePostosChange.bind(this);
    this.handlePostosConfirm = this.handlePostosConfirm.bind(this);
    this.handleMappingChange = this.handleMappingChange.bind(this);
    this.handleMappingConfirm = this.handleMappingConfirm.bind(this);
    this.handleMappingCancel = this.handleMappingCancel.bind(this);
    this.parseCSV = this.parseCSV.bind(this);
    this.processFile = this.processFile.bind(this);
  }

  getProducts() {
    if (this.state.canLoadProducts) {
      this.setState({ canLoadProducts: false });
      const profile = this.context;

      const { history } = this.props;
      productsService
        .get(profile.idEmpresaSec, this.state.idcategory && this.state.idcategory.value ? this.state.idcategory.value : '')
        .then((products) => {

          const enrichedProducts = products.map((product) => ({
            ...product,
            inativo: product.inativo === 't' ? 't' : 'f',
            name: `[${product.idproduct}] ${product.nome}`,
            flags: importantFlags(product.flags),
            costprice: valueToShow(
              product.minValues,
              product.maxValues,
              "costprice",
              false,
              false
            ),
            costprice_iva: valueToShow(
              product.minValues,
              product.maxValues,
              "costprice",
              false,
              true
            ),
            profit: valueToShow(
              product.minValues,
              product.maxValues,
              "profit"
            ),
            profitper: valueToShow(
              product.minValues,
              product.maxValues,
              "profitper"
            ),
            price: valueToShow(
              product.minValues,
              product.maxValues,
              "price",
              true,
              false
            ),
            price_iva: valueToShow(
              product.minValues,
              product.maxValues,
              "price",
              true,
              true
            ),
            iva: valueToShow(
              product.minValues,
              product.maxValues,
              "iva",
              false
            ),
            category: `[${product.idcategoria}] ${product.familia}`,
            edit: (
              <table className="actionButtons" style={{ width: '100%', fontSize: 'xx-small' }}>
                <tr style={{ lineHeight: '10px' }}>
                  {product.composed === 't' ? (<td
                    style={{ cursor: "pointer" }}
                    onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}/composition`)}
                  >
                    <FaClipboardList/>
                    <p>F. Tec.</p>
                  </td>) : null}
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}/stocks`)}
                  >
                    <FaBoxes/>
                    <p>stock</p>
                  </td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}/prices`)}
                  >
                    <MdEuro/>
                    <p>preços</p>
                  </td>
                  <td
                    style={{ fontWeight: 'bolder', fontSize: 'x-small', cursor: "pointer" }}
                    onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}`)}
                  >
                    <FaEdit/>
                    <p>editar</p>
                  </td>
                </tr>
              </table>
            ),
          }));

          this.setState({
            table: {
              ...this.state.table,
              rows: enrichedProducts,
              loading: false,
            },
            loaded: true,
            canLoadProducts: true,
          });
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  getInvalidProducts() {
    const profile = this.context;
    this.setState({ loadingInvalidProducts: true });
    
    productsService
      .getInvalid(profile.idEmpresaSec)
      .then((invalidProducts) => {
        this.setState({ 
          invalidProducts: invalidProducts || [],
          loadingInvalidProducts: false,
          invalidProductsShowLimit: 12 // Resetar o limite quando atualizar
        });
      })
      .catch((error) => {
        console.error('Erro ao obter produtos inválidos:', error);
        this.setState({ 
          invalidProducts: [],
          loadingInvalidProducts: false,
          invalidProductsShowLimit: 12
        });
      });
  }

  loadFunction() {
    const profile = this.context;

    if (!this.state.loaded) {
      postosService
          .get(profile.idEmpresaSec)
          .then(async (warehouses) => {
            await this.setState({ warehouses: warehouses.map((warehouse) => ({ id: warehouse.posto, name: warehouse.nome || warehouse.posto })) });
          });

      apolloService
        .getSync(profile.idEmpresaSec)
        .then((synced) => {
          this.setState({ synced: !!synced.warehouses });
        })
        .catch((error) => {
          console.error(error);
        });

      categoriesService
        .get(profile.idEmpresaSec)
        .then((categories) => {
          const categoriesMap = {};
          categories.forEach((category) => {
            categoriesMap[category.idcategoria] = category;
          });
          
          // Ordenar categorias por ordem alfabética
          const sortedCategories = [...categories].sort((a, b) => {
            const nameA = (a.descricaocategoria || '').toLowerCase();
            const nameB = (b.descricaocategoria || '').toLowerCase();
            return nameA.localeCompare(nameB, 'pt', { sensitivity: 'base' });
          });
          
          this.setState({
            categories: [
              { value: "", label: "TODAS", color: null },
              ...sortedCategories.map((category) => ({
                value: category.idcategoria,
                label: `[${category.idcategoria}] ${category.descricaocategoria}`,
                color: category.color || null,
              })),
            ],
            categoriesMap: categoriesMap,
            loaded: true,
          });
        })
        .catch((error) => {
          console.error(error);
        });

      this.getProducts();
      this.getInvalidProducts();
    }
  }

  handleChange(e, callback) {
    let value = e.target.value;
    let name = e.target.name;

        this.setState({
      [name]: value,
      currentPage: 1,
    }, () => {
        switch (name) {
            case "idcategory":
                localStorage.setItem("products.idcategory", JSON.stringify(value));
                this.getProducts();
                break;
            case "search":
            case "status":
            case "sortBy":
                localStorage.setItem(`products.${name}`, JSON.stringify(value));
                break;
            default:
                break;
            }
    });
  }

  async removeFunction(data) {
    confirmAlert({
      title: "Confirmação",
      message: `Tem a certeza que deseja apagar o produto ${data.name}?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            const profile = this.context;

            productsService
              .unset(profile.idEmpresaSec, data.idproduct)
              .then((response) => {
                if (response !== "NOK") {
                  this.setState({
                    products: this.state.products.filter(
                      (product) => product.idproduct !== data.idproduct
                    ),
                  });
                }
              })
              .catch((error) => {
                console.error(error);
              });
          },
        },
        {
          label: "Não",
          onClick: () => {},
        },
      ],
    });
  }

  async uploadFunction() {
    confirmAlert({
      title: "Confirmação",
      message: `Tem a certeza que deseja enviar todos os produtos para o Apollo?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            const profile = this.context;
            productsService
              .export(profile.idEmpresaSec)
              .catch((error) => {
                console.error(error);
              });
          },
        },
        {
          label: "Não",
          onClick: () => {},
        },
      ],
    });
  }

  handlePostosModalOpen() {
    this.setState({ showPostosModal: true });
  }

  handlePostosModalClose() {
    this.setState({ showPostosModal: false, selectedPostos: [] });
  }

  handlePostosChange(postoId) {
    this.setState((prevState) => {
      const selected = prevState.selectedPostos.includes(postoId)
        ? prevState.selectedPostos.filter((id) => id !== postoId)
        : [...prevState.selectedPostos, postoId];
      return { selectedPostos: selected };
    });
  }

  handlePostosConfirm() {
    if (this.state.selectedPostos.length === 0) {
      alert("Selecione pelo menos um posto.");
      return;
    }
    // Fechar modal de postos e mostrar modal de seleção de ficheiro
    this.setState({ showPostosModal: false, showImportModal: true });
  }

  handleImportClick() {
    // Mostrar modal de seleção de postos primeiro
    this.handlePostosModalOpen();
  }

  handleFileSelect() {
    // Abrir file input
    this.fileInputRef.current.value = null;
    this.fileInputRef.current.click();
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: true });
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
  }

  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        this.processFile(file);
      } else {
        NotificationManager.error('Por favor, selecione um ficheiro CSV válido', "Erro", 5000);
      }
    }
  }

  processFile(file) {
    // Verificar se postos foram selecionados
    if (this.state.selectedPostos.length === 0) {
      NotificationManager.error('Por favor, selecione pelo menos um posto primeiro', "Erro", 5000);
      this.setState({ showImportModal: true, showMappingModal: false });
      return;
    }

    // Fechar modal de importação
    this.setState({ showImportModal: false });

    // Ler o ficheiro CSV
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const { columns, data, separator } = this.parseCSV(csvText);
        
        // Inicializar mapeamento e pré-selecionar colunas com nomes correspondentes
        const columnMapping = {};
        const autoMappedColumns = [];
        
        columns.forEach(col => {
          // Normalizar nome da coluna (trim e lowercase para comparação)
          const normalizedCol = col.trim().toLowerCase();
          
          // Verificar se existe um campo disponível com o mesmo nome
          const matchedField = this.state.availableFields.find(field => {
            if (!field.value || field.value === '') return false;
            return field.value.toLowerCase() === normalizedCol || 
                   field.label.toLowerCase() === normalizedCol;
          });
          
          if (matchedField && matchedField.value) {
            // Pré-selecionar se encontrar correspondência
            columnMapping[col] = matchedField.value;
            autoMappedColumns.push(col);
          } else {
            // Deixar vazio se não encontrar correspondência
            columnMapping[col] = '';
          }
        });

        // Verificar conflitos iniciais se já houver mapeamento de código
        const initialConflicts = [];
        const hasCodigoInMapping = Object.values(columnMapping).includes('codigo');
        if (hasCodigoInMapping) {
          // Verificar conflitos com produtos existentes
          const conflicts = this.checkCodeConflicts(columnMapping, data);
          initialConflicts.push(...conflicts);
        }
        
        this.setState({
          selectedFile: file,
          csvColumns: columns,
          csvData: data,
          csvSeparator: separator,
          columnMapping: columnMapping,
          autoMappedColumns: autoMappedColumns,
          showMappingModal: true,
          codeConflicts: initialConflicts,
        });
      } catch (error) {
        NotificationManager.error('Erro ao ler o ficheiro CSV', "Erro", 5000);
        console.error(error);
      }
    };
    reader.readAsText(file, 'UTF-8');
  }

  parseCSV(text) {
    const lines = text.split(/\r?\n/);
    if (lines.length === 0) return { columns: [], data: [], separator: ',' };

    // Detectar separador (ponto e vírgula ou vírgula)
    const firstLine = lines[0];
    const separator = firstLine.includes(';') ? ';' : ',';

    // Função para dividir CSV respeitando valores entre aspas
    const splitCSVLine = (line, sep) => {
      const result = [];
      let current = '';
      let insideQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          if (insideQuotes && line[i + 1] === '"') {
            // Aspas duplas escapadas
            current += '"';
            i++; // Pular próxima aspas
          } else {
            // Toggle quotes
            insideQuotes = !insideQuotes;
          }
        } else if (char === sep && !insideQuotes) {
          // Separador fora de aspas
          result.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      
      // Adicionar último campo
      result.push(current);
      return result;
    };

    // Função para limpar valores (remover aspas externas e espaços)
    const cleanValue = (value) => {
      if (!value) return '';
      let cleaned = value.trim();
      // Remover aspas apenas se envolverem todo o valor
      if ((cleaned.startsWith('"') && cleaned.endsWith('"')) || 
          (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
        cleaned = cleaned.slice(1, -1);
      }
      // Substituir aspas duplas escapadas por aspas simples
      cleaned = cleaned.replace(/""/g, '"');
      return cleaned.trim();
    };

    // Primeira linha são os headers
    const headerParts = splitCSVLine(firstLine, separator);
    const headers = headerParts.map(cleanValue);
    
    // Resto são os dados
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const values = splitCSVLine(line, separator).map(cleanValue);
      if (values.length === 0 || values.every(v => !v)) continue;
      
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    }

    return { columns: headers, data, separator };
  }

  stringifyCSV(data, headers, separator = ';') {
    // Função para formatar valores CSV com aspas duplas obrigatórias
    const formatCSVValue = (value) => {
      if (value === null || value === undefined) return '""';
      const str = String(value);
      // Escapar aspas duplas internas ("" para representar uma aspas dentro de um valor entre aspas)
      const escaped = str.replace(/"/g, '""');
      // Sempre envolver em aspas duplas
      return `"${escaped}"`;
    };

    // Criar header com formato específico: "codigo";"nome";"iva"
    const headerLine = headers.map(formatCSVValue).join(separator);

    // Criar linhas de dados com o mesmo formato
    const dataLines = data.map(row => 
      headers.map(header => formatCSVValue(row[header] || '')).join(separator)
    );

    // Combinar tudo
    return [headerLine, ...dataLines].join('\n');
  }

  reorganizeCSV(csvData, columnMapping) {
    // Criar novo array de dados com campos reorganizados
    const reorganizedData = csvData.map(row => {
      const newRow = {};
      
      // Para cada campo mapeado, copiar o valor do CSV original
      Object.keys(columnMapping).forEach(csvColumn => {
        const mappedField = columnMapping[csvColumn];
        if (mappedField && mappedField !== '') {
          // Usar o nome do campo como chave no novo CSV
          newRow[mappedField] = row[csvColumn] || '';
        }
      });
      
      return newRow;
    });

    // Criar headers baseados nos campos mapeados (apenas os que não são "Ignorar coluna")
    const headers = Object.values(columnMapping)
      .filter(field => field && field !== '')
      .filter((field, index, self) => self.indexOf(field) === index); // Remover duplicados

    return { data: reorganizedData, headers };
  }

  createCSVFile(csvContent, fileName = 'import.csv') {
    // Criar um Blob com o conteúdo CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Criar um File a partir do Blob
    const file = new File([blob], fileName, { 
      type: 'text/csv;charset=utf-8;',
      lastModified: Date.now()
    });
    
    return file;
  }

  checkCodeConflicts(columnMapping, csvData) {
    // Verificar se o campo 'codigo' está mapeado
    const codigoColumn = Object.keys(columnMapping).find(col => columnMapping[col] === 'codigo');
    if (!codigoColumn) {
      return [];
    }

    // Obter todos os produtos existentes mapeados por código
    const existingProducts = {};
    this.state.table.rows.forEach(product => {
      const code = String(product.idproduct || '').trim();
      if (code) {
        existingProducts[code] = product;
      }
    });

    // Verificar conflitos no CSV
    const conflicts = [];
    csvData.forEach((row, index) => {
      const csvCode = String(row[codigoColumn] || '').trim();
      if (csvCode && existingProducts[csvCode]) {
        // Buscar informações do produto novo do CSV baseado no mapeamento
        const newProductInfo = {};
        Object.keys(columnMapping).forEach(csvCol => {
          const mappedField = columnMapping[csvCol];
          if (mappedField && mappedField !== '') {
            newProductInfo[mappedField] = row[csvCol] || '';
          }
        });

        conflicts.push({
          line: index + 2, // +2 porque linha 1 é o header e index começa em 0
          code: csvCode,
          row: row,
          existingProduct: existingProducts[csvCode],
          newProduct: newProductInfo
        });
      }
    });

    return conflicts;
  }

  async handleFileChange(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Fechar modal de importação se estiver aberto
    if (this.state.showImportModal) {
      this.setState({ showImportModal: false });
    }

    // Processar ficheiro
    this.processFile(file);
  }

  handleMappingChange(columnName, fieldValue) {
    this.setState(prevState => {
      const newMapping = {
        ...prevState.columnMapping,
        [columnName]: fieldValue,
      };
      
      // Verificar conflitos quando o mapeamento muda
      const conflicts = this.checkCodeConflicts(newMapping, prevState.csvData);
      
      return {
        columnMapping: newMapping,
        codeConflicts: conflicts,
      };
    });
  }

  handleMappingCancel() {
    this.setState({
      showMappingModal: false,
      csvColumns: [],
      csvData: [],
      csvSeparator: ';',
      columnMapping: {},
      selectedFile: null,
      codeConflicts: [],
      autoMappedColumns: [],
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = null;
    }
  }

  async handleMappingConfirm() {
    const { selectedFile, codeConflicts, columnMapping } = this.state;
    if (!selectedFile) return;

    // Campos obrigatórios
    const requiredFields = [
      { key: 'codigo', label: 'Código' },
      { key: 'nome', label: 'Nome' },
      { key: 'pvp', label: 'PVP' },
      { key: 'iva', label: 'IVA (%)' },
      { key: 'familia', label: 'Família' },
    ];

    // Verificar quais campos obrigatórios estão mapeados
    const missingFields = requiredFields.filter(field => 
      !Object.values(columnMapping).includes(field.key)
    );

    // Se faltar algum campo obrigatório, mostrar erro
    if (missingFields.length > 0) {
      const missingLabels = missingFields.map(f => f.label).join(', ');
      
      confirmAlert({
        title: "Campos Obrigatórios",
        message: (
          <div>
            <p style={{ color: "#d32f2f", fontWeight: "bold" }}>
              É obrigatório mapear os seguintes campos: <strong>{missingLabels}</strong>.
            </p>
            <p style={{ marginTop: "10px" }}>
              Por favor, selecione colunas do CSV para estes campos antes de continuar.
            </p>
          </div>
        ),
        buttons: [
          {
            label: "OK",
            onClick: () => {},
          },
        ],
      });
      return;
    }

    // Se houver conflitos, pedir confirmação
    if (codeConflicts.length > 0) {
      confirmAlert({
        title: "Conflitos de Código Detectados",
        message: (
          <div>
            <p>Encontrados <strong>{codeConflicts.length}</strong> conflito(s) de código:</p>
            <div style={{ 
              maxHeight: "400px", 
              overflow: "auto", 
              marginTop: "10px",
              padding: "10px",
              backgroundColor: "#f8f9fa",
              borderRadius: "4px",
              fontSize: "12px"
            }}>
              {codeConflicts.map((conflict, index) => {
                const existing = conflict.existingProduct || {};
                const newProd = conflict.newProduct || {};
                
                return (
                  <div key={index} style={{ 
                    marginBottom: "20px", 
                    padding: "15px",
                    backgroundColor: "#fff",
                    borderRadius: "4px",
                    border: "1px solid #ddd"
                  }}>
                    <div style={{ marginBottom: "10px", fontWeight: "bold", color: "#d32f2f" }}>
                      <strong>Linha {conflict.line}:</strong> Código <code style={{ 
                        padding: "2px 6px", 
                        backgroundColor: "#f5f5f5", 
                        borderRadius: "3px",
                        fontSize: "13px"
                      }}>{conflict.code}</code>
                    </div>
                    
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                      {/* Produto Existente */}
                      <div style={{ 
                        padding: "10px",
                        backgroundColor: "#fff3cd",
                        borderRadius: "4px",
                        border: "1px solid #ffc107"
                      }}>
                        <div style={{ 
                          fontWeight: "bold", 
                          marginBottom: "8px",
                          color: "#856404",
                          borderBottom: "1px solid #ffc107",
                          paddingBottom: "5px"
                        }}>
                          Produto Existente
                        </div>
                        <div style={{ fontSize: "11px", color: "#666" }}>
                          <div><strong>Nome:</strong> {existing.nome || '-'}</div>
                          <div><strong>PVP:</strong> {existing.values?.[0]?.price ? `${existing.values[0].price}` : '-'}</div>
                          <div><strong>IVA:</strong> {existing.values?.[0]?.iva || '-'}</div>
                          <div><strong>Família:</strong> {existing.familia || '-'}</div>
                          {existing.cbarras1 && <div><strong>Código Barras:</strong> {existing.cbarras1}</div>}
                        </div>
                      </div>
                      
                      {/* Produto Novo */}
                      <div style={{ 
                        padding: "10px",
                        backgroundColor: "#e3f2fd",
                        borderRadius: "4px",
                        border: "1px solid #2196f3"
                      }}>
                        <div style={{ 
                          fontWeight: "bold", 
                          marginBottom: "8px",
                          color: "#1565c0",
                          borderBottom: "1px solid #2196f3",
                          paddingBottom: "5px"
                        }}>
                          Produto Novo (CSV)
                        </div>
                        <div style={{ fontSize: "11px", color: "#666" }}>
                          <div><strong>Nome:</strong> {newProd.nome || '-'}</div>
                          <div><strong>PVP:</strong> {newProd.pvp || '-'}</div>
                          <div><strong>IVA:</strong> {newProd.iva || '-'}</div>
                          <div><strong>Família:</strong> {newProd.familia || '-'}</div>
                          {(newProd.cbarras1 || newProd.cbarras2 || newProd.cbarras3) && (
                            <div><strong>Código Barras:</strong> {
                              [newProd.cbarras1, newProd.cbarras2, newProd.cbarras3]
                                .filter(Boolean)
                                .join(', ') || '-'
                            }</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p style={{ marginTop: "15px", fontWeight: "bold" }}>Estes registos serão <strong>atualizados</strong> se continuar.</p>
            <p>Deseja continuar com a importação?</p>
          </div>
        ),
        buttons: [
          {
            label: "Sim, continuar",
            onClick: () => this.performImport(),
          },
          {
            label: "Cancelar",
            onClick: () => {},
          },
        ],
      });
      return;
    }

    // Se não houver conflitos, importar diretamente
    this.performImport();
  }

  async performImport() {
    const { csvData, columnMapping, selectedPostos } = this.state;
    if (!csvData || csvData.length === 0) return;

    const profile = this.context;
    const postos = selectedPostos.length > 0 ? selectedPostos : [1];

    // Reorganizar CSV baseado no mapeamento
    const { data: reorganizedData, headers } = this.reorganizeCSV(csvData, columnMapping);

    // Gerar CSV corrigido com formato específico: ponto e vírgula e aspas duplas
    const correctedCSV = this.stringifyCSV(reorganizedData, headers, ';');

    // Criar novo ficheiro CSV corrigido
    const correctedFile = this.createCSVFile(correctedCSV, 'import_corrected.csv');

    // Criar FormData com o ficheiro corrigido
    const formData = new FormData();
    formData.append('idempresa', profile.idEmpresaSec);
    formData.append('postos', postos.join(','));
    formData.append('file', correctedFile);

    this.setState({ showMappingModal: false });

    try {
      const response = await productsService.import(formData);
      if (response.imported) {
        NotificationManager.success(`${response.imported} produto(s)`, "Importação de Produtos", 5000);
      } else if (response.error) {
        NotificationManager.error(`Erro: ${response.error}`, "Erro", 5000);
      } else if (response === 'NOK') {
        NotificationManager.error('Erro ao importar produtos', "Erro", 5000);
      } else {
        NotificationManager.info('Importação concluída.', "Importação de Produtos", 5000);
      }
      this.getProducts();
    } catch (error) {
      NotificationManager.error('Erro ao importar produtos', "Erro", 5000);
      console.error(error);
    }

    // Reset state
    this.setState({
      csvColumns: [],
      csvData: [],
      csvSeparator: ';',
      columnMapping: {},
      selectedFile: null,
      codeConflicts: [],
      autoMappedColumns: [],
      selectedPostos: [],
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = null;
    }
  }

  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  }

  handleItemsPerPageChange = (e) => {
    this.setState({ 
      itemsPerPage: parseInt(e.target.value),
      currentPage: 1 
    });
  }

  render() {
    const { synced, table, idcategory, categories, categoriesMap, search, status, allStatus, sortBy, allSortBy, showPostosModal, showImportModal, showMappingModal, warehouses, selectedPostos, currentPage, itemsPerPage, csvColumns, csvData, columnMapping, availableFields, isDragging, invalidProducts, loadingInvalidProducts, invalidProductsShowLimit } = this.state;
    const { location, history } = this.props;
    const cookies = new URLSearchParams(location.search);

    const filterSearch = search.split(" ");

    const filteredProducts = table.rows
      .filter((product) =>
        idcategory.value
          ? product.idcategoria === idcategory.value
          : true
      )
      .filter((product) =>
        filterSearch.every(
          (eachSearch) =>
            product.name
              .toLowerCase()
              .includes(eachSearch.toLowerCase()) ||
            product.barcodes
              .toLowerCase()
              .includes(eachSearch.toLowerCase()) ||
            product.idproduct
              .toLowerCase()
              .includes(eachSearch.toLowerCase())
        )
      )
      .filter((product) =>
        status.value !== ''
          ? product.inativo === status.value
          : true
      )
      .sort((a, b) => {
        // Ordenar conforme o filtro selecionado
        const { sortBy } = this.state;
        const sortValue = sortBy.value || 'name-asc';

        switch (sortValue) {
          case 'name-asc':
            const nameA = (a.nome || a.name || '').toLowerCase();
            const nameB = (b.nome || b.name || '').toLowerCase();
            return nameA.localeCompare(nameB, 'pt', { sensitivity: 'base' });
          
          case 'name-desc':
            const nameA_desc = (a.nome || a.name || '').toLowerCase();
            const nameB_desc = (b.nome || b.name || '').toLowerCase();
            return nameB_desc.localeCompare(nameA_desc, 'pt', { sensitivity: 'base' });
          
          case 'price-desc':
            // Para ordenar do mais caro para o mais barato, usar o valor máximo do intervalo
            const priceA_interval = parseInterval(a.price_iva);
            const priceB_interval = parseInterval(b.price_iva);
            const priceA_desc = priceA_interval.max !== null ? priceA_interval.max : (priceA_interval.min !== null ? priceA_interval.min : 0);
            const priceB_desc = priceB_interval.max !== null ? priceB_interval.max : (priceB_interval.min !== null ? priceB_interval.min : 0);
            return priceB_desc - priceA_desc;
          
          case 'price-asc':
            // Para ordenar do mais barato para o mais caro, usar o valor mínimo do intervalo
            const priceA_asc_interval = parseInterval(a.price_iva);
            const priceB_asc_interval = parseInterval(b.price_iva);
            const priceA_asc = priceA_asc_interval.min !== null ? priceA_asc_interval.min : (priceA_asc_interval.max !== null ? priceA_asc_interval.max : null);
            const priceB_asc = priceB_asc_interval.min !== null ? priceB_asc_interval.min : (priceB_asc_interval.max !== null ? priceB_asc_interval.max : null);
            // Valores inválidos vão para o final
            if (priceA_asc === null && priceB_asc === null) return 0;
            if (priceA_asc === null) return 1;
            if (priceB_asc === null) return -1;
            return priceA_asc - priceB_asc;
          
          case 'profit-desc':
            // Para ordenar do mais lucrativo para o menos lucrativo, usar o valor máximo do intervalo
            const profitA_desc_interval = parseInterval(a.profit);
            const profitB_desc_interval = parseInterval(b.profit);
            const profitA_desc = profitA_desc_interval.max !== null ? profitA_desc_interval.max : (profitA_desc_interval.min !== null ? profitA_desc_interval.min : null);
            const profitB_desc = profitB_desc_interval.max !== null ? profitB_desc_interval.max : (profitB_desc_interval.min !== null ? profitB_desc_interval.min : null);
            // Valores inválidos vão para o final
            if (profitA_desc === null && profitB_desc === null) return 0;
            if (profitA_desc === null) return 1;
            if (profitB_desc === null) return -1;
            return profitB_desc - profitA_desc;
          
          case 'profit-asc':
            // Para ordenar do menos lucrativo para o mais lucrativo, usar o valor mínimo do intervalo
            const profitA_asc_interval = parseInterval(a.profit);
            const profitB_asc_interval = parseInterval(b.profit);
            const profitA_asc = profitA_asc_interval.min !== null ? profitA_asc_interval.min : (profitA_asc_interval.max !== null ? profitA_asc_interval.max : null);
            const profitB_asc = profitB_asc_interval.min !== null ? profitB_asc_interval.min : (profitB_asc_interval.max !== null ? profitB_asc_interval.max : null);
            // Valores inválidos vão para o final
            if (profitA_asc === null && profitB_asc === null) return 0;
            if (profitA_asc === null) return 1;
            if (profitB_asc === null) return -1;
            return profitA_asc - profitB_asc;
          
          default:
            const nameA_default = (a.nome || a.name || '').toLowerCase();
            const nameB_default = (b.nome || b.name || '').toLowerCase();
            return nameA_default.localeCompare(nameB_default, 'pt', { sensitivity: 'base' });
        }
      });

    // Cálculos de estatísticas
    const totalProducts = table.rows.length;
    const activeProducts = table.rows.filter(p => p.inativo !== 't').length;
    const inactiveProducts = table.rows.filter(p => p.inativo === 't').length;
    const filteredCount = filteredProducts.length;

    // Calcular produto mais caro e mais barato (baseado no preço de venda com IVA)
    // Usar filteredProducts para refletir os filtros aplicados
    // Para produtos com variação de preço, usar maxValues para mais caro e minValues para mais barato
    // Filtrar produtos que têm preço com IVA válido
    const productsWithPrice = filteredProducts.filter(p => {
      if (p.price_iva && p.inativo !== 't') {
        const priceStr = String(p.price_iva).trim();
        if (priceStr.includes(' - ')) {
          const maxPrice = parseFloat(priceStr.split(' - ').pop());
          return !isNaN(maxPrice) && maxPrice > 0;
        } else {
          const price = parseFloat(priceStr);
          return !isNaN(price) && price > 0;
        }
      }
      return false;
    });

    const mostExpensive = productsWithPrice.length > 0 
      ? productsWithPrice.reduce((max, p) => {
          // Usar price_iva que já está calculado corretamente
          const pInterval = parseInterval(p.price_iva);
          const maxInterval = parseInterval(max.price_iva);
          const maxPrice = pInterval.max !== null ? pInterval.max : (pInterval.min !== null ? pInterval.min : 0);
          const currentMaxPrice = maxInterval.max !== null ? maxInterval.max : (maxInterval.min !== null ? maxInterval.min : 0);
          return maxPrice > currentMaxPrice ? p : max;
        }, productsWithPrice[0])
      : null;

    const cheapest = productsWithPrice.length > 0
      ? productsWithPrice.reduce((min, p) => {
          // Usar price_iva que já está calculado corretamente
          const pInterval = parseInterval(p.price_iva);
          const minInterval = parseInterval(min.price_iva);
          const minPrice = pInterval.min !== null ? pInterval.min : (pInterval.max !== null ? pInterval.max : Infinity);
          const currentMinPrice = minInterval.min !== null ? minInterval.min : (minInterval.max !== null ? minInterval.max : Infinity);
          return minPrice < currentMinPrice ? p : min;
        }, productsWithPrice[0])
      : null;

    // Calcular produto mais lucrativo e menos lucrativo (baseado na margem s/IVA)
    // Usar filteredProducts para refletir os filtros aplicados
    // Incluir produtos com margens negativas para encontrar o menos lucrativo
    const productsWithProfit = filteredProducts.filter(p => {
      if (p.profit && p.inativo !== 't') {
        const interval = parseInterval(p.profit);
        return (interval.min !== null && !isNaN(interval.min) && isFinite(interval.min)) || 
               (interval.max !== null && !isNaN(interval.max) && isFinite(interval.max));
      }
      return false;
    });

    const mostProfitable = productsWithProfit.length > 0
      ? productsWithProfit.reduce((max, p) => {
          // Extrair o valor máximo do intervalo de margem
          const pInterval = parseInterval(p.profit);
          const maxInterval = parseInterval(max.profit);
          const maxProfit = pInterval.max !== null ? pInterval.max : (pInterval.min !== null ? pInterval.min : null);
          const currentMaxProfit = maxInterval.max !== null ? maxInterval.max : (maxInterval.min !== null ? maxInterval.min : null);
          
          // Validar valores
          if (maxProfit === null || isNaN(maxProfit) || !isFinite(maxProfit)) {
            return max; // Manter o máximo atual se este valor é inválido
          }
          if (currentMaxProfit === null || isNaN(currentMaxProfit) || !isFinite(currentMaxProfit)) {
            return p; // Usar o novo se o atual é inválido
          }
          
          return maxProfit > currentMaxProfit ? p : max;
        }, productsWithProfit[0])
      : null;

    const leastProfitable = productsWithProfit.length > 0
      ? productsWithProfit.reduce((min, p) => {
          // Extrair o valor mínimo do intervalo de margem
          const pInterval = parseInterval(p.profit);
          const minInterval = parseInterval(min.profit);
          const minProfit = pInterval.min !== null ? pInterval.min : (pInterval.max !== null ? pInterval.max : null);
          const currentMinProfit = minInterval.min !== null ? minInterval.min : (minInterval.max !== null ? minInterval.max : null);
          
          // Validar valores
          if (minProfit === null || isNaN(minProfit) || !isFinite(minProfit)) {
            return min; // Manter o mínimo atual se este valor é inválido
          }
          if (currentMinProfit === null || isNaN(currentMinProfit) || !isFinite(currentMinProfit)) {
            return p; // Usar o novo se o atual é inválido
          }
          
          return minProfit < currentMinProfit ? p : min;
        }, productsWithProfit[0])
      : null;

    // Cálculos de paginação
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

    return (
      <div className="products-list-page">
        <Menu
          loadFunction={this.loadFunction}
          location={location}
          history={history}
          newCookies={cookies}
        />
        <div className="fullContainer" style={{ maxWidth: "100%" }}>
          <div className="col-xs-12">
            <ol
              style={{
                textAlign: "left",
                marginTop: "75px",
              }}
              className="breadcrumb"
            >
              <li>
                <a href="http://eposgestofinal.com/index.php">Portal</a>
              </li>
              <li className="active">Produtos</li>
            </ol>
          </div>
        </div>
        <EmpresaSelect />
        <div className="fullContainer" style={{ maxWidth: "100%" }}>
          <div className="col-sm-12" style={{ textAlign: "center" }}>
            {/* Secção de Estatísticas */}
            <div className="products-stats-section">
              <div className="products-stats-grid">
                <div className="products-stat-card">
                  <div className="products-stat-icon">
                    <i className="fas fa-boxes"></i>
                  </div>
                  <div className="products-stat-content">
                    <div className="products-stat-label">Total de Produtos</div>
                    <div className="products-stat-value">{totalProducts}</div>
                  </div>
                </div>
                <div className="products-stat-card products-stat-card-success">
                  <div className="products-stat-icon">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <div className="products-stat-content">
                    <div className="products-stat-label">Produtos Ativos</div>
                    <div className="products-stat-value">{activeProducts}</div>
                  </div>
                </div>
                <div className="products-stat-card products-stat-card-danger">
                  <div className="products-stat-icon">
                    <i className="fas fa-times-circle"></i>
                  </div>
                  <div className="products-stat-content">
                    <div className="products-stat-label">Produtos Inativos</div>
                    <div className="products-stat-value">{inactiveProducts}</div>
                  </div>
                </div>
                <div className="products-stat-card products-stat-card-info">
                  <div className="products-stat-icon">
                    <i className="fas fa-filter"></i>
                  </div>
                  <div className="products-stat-content">
                    <div className="products-stat-label">Resultados Filtrados</div>
                    <div className="products-stat-value">{filteredCount}</div>
                  </div>
                </div>
                <div className="products-stat-card products-stat-card-warning">
                  <div className="products-stat-icon">
                    <i className="fas fa-arrow-up"></i>
                  </div>
                  <div className="products-stat-content">
                    <div className="products-stat-label">Produto Mais Caro</div>
                    <div className="products-stat-value">
                      {mostExpensive ? (() => {
                        const interval = parseInterval(mostExpensive.price_iva);
                        const maxPrice = interval.max !== null ? interval.max : (interval.min !== null ? interval.min : null);
                        if (maxPrice !== null && !isNaN(maxPrice) && isFinite(maxPrice) && maxPrice > 0) {
                          return milhares(maxPrice);
                        }
                        return '-';
                      })() : '-'}
                    </div>
                    {mostExpensive && (
                      <div className="products-stat-product-name" title={mostExpensive.name || mostExpensive.nome}>
                        {mostExpensive.name || mostExpensive.nome}
                      </div>
                    )}
                  </div>
                </div>
                <div className="products-stat-card products-stat-card-secondary">
                  <div className="products-stat-icon">
                    <i className="fas fa-arrow-down"></i>
                  </div>
                  <div className="products-stat-content">
                    <div className="products-stat-label">Produto Mais Barato</div>
                    <div className="products-stat-value">
                      {cheapest ? (() => {
                        const interval = parseInterval(cheapest.price_iva);
                        const minPrice = interval.min !== null ? interval.min : (interval.max !== null ? interval.max : null);
                        if (minPrice !== null && !isNaN(minPrice) && isFinite(minPrice) && minPrice > 0) {
                          return milhares(minPrice);
                        }
                        return '-';
                      })() : '-'}
                    </div>
                    {cheapest && (
                      <div className="products-stat-product-name" title={cheapest.name || cheapest.nome}>
                        {cheapest.name || cheapest.nome}
                      </div>
                    )}
                  </div>
                </div>
                <div className="products-stat-card products-stat-card-success-alt">
                  <div className="products-stat-icon">
                    <i className="far fa-thumbs-up"></i>
                  </div>
                  <div className="products-stat-content">
                    <div className="products-stat-label">Mais Lucrativo</div>
                    <div className="products-stat-value">
                      {mostProfitable ? (() => {
                        const interval = parseInterval(mostProfitable.profit);
                        const maxProfit = interval.max !== null ? interval.max : (interval.min !== null ? interval.min : null);
                        if (maxProfit !== null && !isNaN(maxProfit) && isFinite(maxProfit)) {
                          return milhares(maxProfit);
                        }
                        return '-';
                      })() : '-'}
                    </div>
                    {mostProfitable && (
                      <div className="products-stat-product-name" title={mostProfitable.name || mostProfitable.nome}>
                        {mostProfitable.name || mostProfitable.nome}
                      </div>
                    )}
                  </div>
                </div>
                <div className="products-stat-card products-stat-card-danger-alt">
                  <div className="products-stat-icon">
                    <i className="far fa-thumbs-down"></i>
                  </div>
                  <div className="products-stat-content">
                    <div className="products-stat-label">Menos Lucrativo</div>
                    <div className="products-stat-value">
                      {leastProfitable ? (() => {
                        const interval = parseInterval(leastProfitable.profit);
                        const minProfit = interval.min !== null ? interval.min : (interval.max !== null ? interval.max : null);
                        if (minProfit !== null && !isNaN(minProfit) && isFinite(minProfit)) {
                          return milhares(minProfit);
                        }
                        return '-';
                      })() : '-'}
                    </div>
                    {leastProfitable && (
                      <div className="products-stat-product-name" title={leastProfitable.name || leastProfitable.nome}>
                        {leastProfitable.name || leastProfitable.nome}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Secção de Produtos com Erros */}
            {invalidProducts && invalidProducts.length > 0 && (
              <div className="products-invalid-section" style={{
                marginBottom: "30px",
                padding: "20px",
                backgroundColor: "#fff3cd",
                borderRadius: "8px",
                border: "2px solid #ffc107",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "15px"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}>
                    <i className="fas fa-exclamation-triangle" style={{
                      fontSize: "24px",
                      color: "#856404"
                    }}></i>
                    <h4 style={{
                      margin: 0,
                      color: "#856404",
                      fontWeight: "600"
                    }}>
                      Produtos com Erros ({invalidProducts.length})
                    </h4>
                  </div>
                  <button
                    className="btn btn-sm"
                    onClick={this.getInvalidProducts}
                    disabled={loadingInvalidProducts}
                    style={{
                      backgroundColor: "#ffc107",
                      color: "#856404",
                      border: "1px solid #ffc107",
                      padding: "6px 12px",
                      borderRadius: "4px",
                      cursor: loadingInvalidProducts ? "not-allowed" : "pointer",
                      opacity: loadingInvalidProducts ? 0.6 : 1
                    }}
                    title="Atualizar lista"
                  >
                    <i className={`fas ${loadingInvalidProducts ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} style={{ marginRight: "5px" }}></i>
                    Atualizar
                  </button>
                </div>
                <p style={{
                  margin: "0 0 15px 0",
                  color: "#856404",
                  fontSize: "14px"
                }}>
                  Os seguintes produtos têm erros que precisam de ser corrigidos:
                </p>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                  gap: "10px"
                }}>
                  {invalidProducts.slice(0, invalidProductsShowLimit).map((product) => (
                    <a
                      key={product.idproduct}
                      href={`/products/${encodeURIComponent(product.idproduct)}/prices`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "12px 15px",
                        backgroundColor: "#fff",
                        borderRadius: "6px",
                        border: "1px solid #ffc107",
                        textDecoration: "none",
                        color: "#856404",
                        transition: "all 0.2s",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#fffbf0";
                        e.currentTarget.style.borderColor = "#ff9800";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff";
                        e.currentTarget.style.borderColor = "#ffc107";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <i className="fas fa-exclamation-circle" style={{
                        fontSize: "18px",
                        color: "#dc3545",
                        flexShrink: 0
                      }}></i>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: "600",
                          fontSize: "14px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}>
                          [{product.idproduct}] {product.nome}
                        </div>
                        <div style={{
                          fontSize: "12px",
                          color: "#666",
                          marginTop: "2px"
                        }}>
                          Clique para corrigir
                        </div>
                      </div>
                      <i className="fas fa-chevron-right" style={{
                        fontSize: "12px",
                        color: "#856404",
                        flexShrink: 0
                      }}></i>
                    </a>
                  ))}
                </div>
                {invalidProducts.length > invalidProductsShowLimit && (
                  <div style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "20px"
                  }}>
                    <button
                      className="btn"
                      onClick={() => this.setState({ invalidProductsShowLimit: invalidProducts.length })}
                      style={{
                        backgroundColor: "#ffc107",
                        color: "#856404",
                        border: "1px solid #ffc107",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: "600",
                        fontSize: "14px",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffb300";
                        e.currentTarget.style.borderColor = "#ffb300";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffc107";
                        e.currentTarget.style.borderColor = "#ffc107";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <i className="fas fa-chevron-down" style={{ marginRight: "8px" }}></i>
                      Ver Mais ({invalidProducts.length - invalidProductsShowLimit} restantes)
                    </button>
                  </div>
                )}
                {invalidProducts.length > 12 && invalidProductsShowLimit >= invalidProducts.length && (
                  <div style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "20px"
                  }}>
                    <button
                      className="btn"
                      onClick={() => this.setState({ invalidProductsShowLimit: 12 })}
                      style={{
                        backgroundColor: "#6c757d",
                        color: "#fff",
                        border: "1px solid #6c757d",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: "600",
                        fontSize: "14px",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#5a6268";
                        e.currentTarget.style.borderColor = "#5a6268";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#6c757d";
                        e.currentTarget.style.borderColor = "#6c757d";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <i className="fas fa-chevron-up" style={{ marginRight: "8px" }}></i>
                      Ver Menos
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <div className="products-filters-section">
                <div className="products-filters-section-title">
                  <div className="products-filters-title-left">
                    <i className="fas fa-filter"></i>
                    <span>Filtros</span>
                  </div>
                  <div className="products-header-actions">
                    <button
                      className="products-header-btn"
                      onClick={() => history.push("/products/pricetags")}
                      title="Etiquetas"
                    >
                      <i className="fas fa-tags"></i>
                      <span>Etiquetas</span>
                    </button>
                    {synced && (
                      <button
                        className="products-header-btn"
                        onClick={() => history.push("/products/sync")}
                        title="Enviar Combustíveis"
                      >
                        <i className="fas fa-gas-pump"></i>
                        <span>Enviar Combustíveis</span>
                      </button>
                    )}
                    <button
                      className="products-header-btn"
                      onClick={this.uploadFunction}
                      title="Enviar Produtos"
                    >
                      <i className="fas fa-upload"></i>
                      <span>Enviar Produtos</span>
                    </button>
                    <button
                      className="products-header-btn"
                      onClick={this.handleImportClick}
                      title="Importar Produtos"
                    >
                      <i className="fas fa-file-import"></i>
                      <span>Importar Produtos</span>
                    </button>
                    <button
                      className="products-header-btn products-header-btn-primary"
                      onClick={() => history.push("/products/new")}
                      title="Adicionar Produto"
                    >
                      <MdAddCircleOutline />
                      <span>Adicionar Produto</span>
                    </button>
                  </div>
                </div>
                <input
                  type="file"
                  accept=".csv"
                  ref={this.fileInputRef}
                  style={{ display: "none" }}
                  onChange={this.handleFileChange}
                />
                {/* Simple postos selection modal */}
                {showPostosModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      background: "rgba(0,0,0,0.3)",
                      zIndex: 1000,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        padding: 20,
                        borderRadius: 8,
                        minWidth: 300,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
                      }}
                    >
                      <h4>Selecione os postos</h4>
                      <div style={{ marginBottom: 10 }}>
                        <table style={{ width: "100%" }}>
                          {warehouses.map((posto) => (
                            <tr style={{ color: selectedPostos.includes(posto.id) ? 'green' : 'black' }} key={posto.id}>
                              <td>
                                <Switch
                                  checked={selectedPostos.includes(posto.id)}
                                  handleChange={() => this.handlePostosChange(posto.id)}
                                />          
                              </td>
                              <td>
                                [{posto.id}] {posto.name}
                              </td>
                            </tr>
                          ))}
                        </table>
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <button className="btn btn-secondary" onClick={this.handlePostosModalClose}>
                          Cancelar
                        </button>
                        <button className="btn btn-primary" onClick={this.handlePostosConfirm}>
                          Continuar
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Modal de seleção de ficheiro CSV */}
                {showImportModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      background: "rgba(0,0,0,0.3)",
                      zIndex: 1000,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        this.setState({ showImportModal: false, isDragging: false });
                      }
                    }}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onDragOver={this.handleDragOver}
                      onDragLeave={this.handleDragLeave}
                      onDrop={this.handleDrop}
                      style={{
                        background: "#fff",
                        padding: 30,
                        borderRadius: 8,
                        minWidth: 500,
                        maxWidth: "90vw",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        border: isDragging ? "2px dashed #2196f3" : "none",
                        backgroundColor: isDragging ? "#e3f2fd" : "#fff"
                      }}
                    >
                      <h3 style={{ marginTop: 0, marginBottom: 20 }}>Importar Produtos</h3>
                      <p style={{ fontSize: "14px", color: "#666", marginBottom: 25 }}>
                        Selecione ou arraste um ficheiro CSV para importar produtos.
                      </p>
                      <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
                        <button
                          className="btn btn-primary"
                          onClick={this.handleFileSelect}
                          style={{ width: "100%", padding: "12px" }}
                        >
                          <i className="fas fa-file-upload" style={{ marginRight: 8 }}></i>
                          Selecionar Ficheiro CSV
                        </button>
                      </div>
                      <div style={{ marginTop: 20, padding: 15, backgroundColor: "#f8f9fa", borderRadius: "4px", fontSize: "12px" }}>
                        <strong>Dica:</strong> O ficheiro CSV deve conter pelo menos as colunas: <strong>Código, Nome, PVP, IVA (%) e Família</strong>.
                      </div>
                    </div>
                  </div>
                )}
                {/* Modal de mapeamento de colunas CSV */}
                {showMappingModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      background: "rgba(0,0,0,0.3)",
                      zIndex: 1001,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "auto"
                    }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        this.handleMappingCancel();
                      }
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        padding: 20,
                        borderRadius: 8,
                        minWidth: 600,
                        maxWidth: "90vw",
                        maxHeight: "90vh",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        overflow: "auto"
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h4 style={{ marginTop: 0 }}>Mapear Colunas do CSV</h4>
                      <p style={{ fontSize: "14px", color: "#666", marginBottom: 20 }}>
                        Mapeie cada coluna do ficheiro CSV para o campo correspondente na base de dados.
                      </p>
                      <div style={{ marginBottom: 20, maxHeight: "400px", overflow: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ backgroundColor: "#f5f5f5" }}>
                              <th style={{ padding: "10px", textAlign: "left", border: "1px solid #ddd" }}>
                                Coluna do CSV
                              </th>
                              <th style={{ padding: "10px", textAlign: "left", border: "1px solid #ddd" }}>
                                Exemplo de valor
                              </th>
                              <th style={{ padding: "10px", textAlign: "left", border: "1px solid #ddd" }}>
                                Mapear para campo
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {csvColumns.map((column, index) => {
                              const sampleValue = csvData.length > 0 && csvData[0][column] 
                                ? (String(csvData[0][column]).length > 30 
                                  ? String(csvData[0][column]).substring(0, 30) + '...' 
                                  : String(csvData[0][column]))
                                : '(vazio)';
                              const isAutoMapped = this.state.autoMappedColumns.includes(column);
                              return (
                                <tr 
                                  key={index}
                                  style={{
                                    backgroundColor: isAutoMapped ? '#e8f5e9' : '#fff',
                                    borderLeft: isAutoMapped ? '3px solid #4caf50' : 'none'
                                  }}
                                >
                                  <td style={{ 
                                    padding: "10px", 
                                    border: "1px solid #ddd", 
                                    fontWeight: "500",
                                    position: "relative"
                                  }}>
                                    {isAutoMapped && (
                                      <span 
                                        style={{
                                          position: "absolute",
                                          left: "-10px",
                                          top: "50%",
                                          transform: "translateY(-50%)",
                                          fontSize: "16px",
                                          color: "#4caf50"
                                        }}
                                        title="Pré-selecionado automaticamente"
                                      >
                                        ✓
                                      </span>
                                    )}
                                    {column}
                                  </td>
                                  <td style={{ padding: "10px", border: "1px solid #ddd", fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                                    {sampleValue}
                                  </td>
                                  <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                                    <select
                                      value={columnMapping[column] || ''}
                                      onChange={(e) => {
                                        this.handleMappingChange(column, e.target.value);
                                        // Remover da lista de auto-mapeados se alterar
                                        if (isAutoMapped && e.target.value !== columnMapping[column]) {
                                          this.setState(prevState => ({
                                            autoMappedColumns: prevState.autoMappedColumns.filter(col => col !== column)
                                          }));
                                        }
                                      }}
                                      style={{
                                        width: "100%",
                                        padding: "8px",
                                        borderRadius: "4px",
                                        border: isAutoMapped ? "1px solid #4caf50" : "1px solid #ddd",
                                        backgroundColor: isAutoMapped ? '#f1f8f4' : '#fff'
                                      }}
                                    >
                                      {availableFields.map((field, fieldIndex) => (
                                        <option key={fieldIndex} value={field.value}>
                                          {field.label}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ 
                        marginTop: 20, 
                        padding: 15, 
                        backgroundColor: "#f8f9fa", 
                        borderRadius: "4px",
                        fontSize: "12px"
                      }}>
                        <strong>Dica:</strong> Se uma coluna não corresponder a nenhum campo, selecione "Ignorar coluna".
                      </div>
                      {this.state.autoMappedColumns.length > 0 && (
                        <div style={{
                          marginBottom: 20,
                          padding: 10,
                          backgroundColor: "#e8f5e9",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#2e7d32"
                        }}>
                          <i className="fas fa-info-circle" style={{ marginRight: 8 }}></i>
                          <strong>{this.state.autoMappedColumns.length}</strong> coluna(s) foram pré-selecionadas automaticamente (marcadas com ✓ e fundo verde).
                        </div>
                      )}
                      {(() => {
                        const requiredFields = [
                          { key: 'codigo', label: 'Código' },
                          { key: 'nome', label: 'Nome' },
                          { key: 'pvp', label: 'PVP' },
                          { key: 'iva', label: 'IVA (%)' },
                          { key: 'familia', label: 'Família' },
                        ];
                        const missingFields = requiredFields.filter(field => 
                          !Object.values(columnMapping).includes(field.key)
                        );
                        const hasAllRequired = missingFields.length === 0;

                        return (
                          <>
                            {missingFields.length > 0 && (
                              <div style={{ 
                                marginTop: 15, 
                                padding: 15, 
                                backgroundColor: "#ffebee", 
                                border: "1px solid #d32f2f",
                                borderRadius: "4px",
                                fontSize: "13px",
                                color: "#c62828"
                              }}>
                                <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }}></i>
                                <strong>Atenção:</strong> É <strong>obrigatório</strong> mapear os seguintes campos: <strong>Código, Nome, PVP, IVA (%) e Família</strong>.
                                <div style={{ marginTop: 10 }}>
                                  {missingFields.map((field, index) => (
                                    <span key={index} style={{ display: 'block', marginTop: 5 }}>
                                      • Falta mapear: <strong>{field.label}</strong>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
                              <button 
                                className="btn btn-secondary" 
                                onClick={this.handleMappingCancel}
                              >
                                Cancelar
                              </button>
                              <button 
                                className="btn btn-primary" 
                                onClick={this.handleMappingConfirm}
                                disabled={!hasAllRequired}
                                style={{
                                  opacity: !hasAllRequired ? 0.5 : 1,
                                  cursor: !hasAllRequired ? 'not-allowed' : 'pointer'
                                }}
                              >
                                Importar
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
                <div className="products-filters-grid">
                  <div className="products-filter-item">
                    <label className="products-filter-label">Ordenar por</label>
                    <AutoComplete
                      value={sortBy}
                      options={allSortBy}
                      name="sortBy"
                      handleChange={this.handleChange}
                      placeholder="Selecione..."
                    />
                  </div>
                  <div className="products-filter-item">
                    <label className="products-filter-label">Família</label>
                    <AutoComplete
                      name={"idcategory"}
                      value={idcategory}
                      handleChange={this.handleChange}
                      options={categories}
                      formatOptionLabel={(option) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {option.color && (
                            <span
                              style={{
                                display: 'inline-block',
                                width: '12px',
                                height: '12px',
                                borderRadius: '2px',
                                backgroundColor: normalizeColor(option.color),
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <span>{option.label}</span>
                        </div>
                      )}
                    />
                  </div>
                  <div className="products-filter-item">
                    <label className="products-filter-label">Pesquisa</label>
                    <Input
                      inputType={"text"}
                      name={"search"}
                      value={search}
                      handleChange={this.handleChange}
                      placeholder="Pesquisar produtos..."
                    />
                  </div>
                  <div className="products-filter-item">
                    <label className="products-filter-label">Estado</label>
                    <AutoComplete
                      name={"status"}
                      value={status} 
                      handleChange={this.handleChange}
                      options={allStatus}
                      formatOptionLabel={(option) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {option.value === 'f' && (
                            <span
                              style={{
                                display: 'inline-block',
                                width: '12px',
                                height: '12px',
                                borderRadius: '2px',
                                backgroundColor: '#28a745',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {option.value === 't' && (
                            <span
                              style={{
                                display: 'inline-block',
                                width: '12px',
                                height: '12px',
                                borderRadius: '2px',
                                backgroundColor: '#dc3545',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <span>{option.label}</span>
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>
            {/* Vista Mobile - Cards */}
            <div className="products-cards-list">
                {table.loading ? (
                  <div style={{ textAlign: "center", padding: "40px" }}>
                    <p>A carregar produtos...</p>
                  </div>
                ) : paginatedProducts.length === 0 ? (
                  <div className="products-empty-state">
                    <p>Nenhum produto encontrado.</p>
                  </div>
                ) : (
                  <>
                    {paginatedProducts.map((product) => (
                      <div key={product.idproduct} className="product-card">
                        <div className="product-card-header">
                          <div className="product-card-image-name">
                            <img
                              src={product.image ? `https://eposgestofinal.com/${product.image}` : 'https://eposgestofinal.com/site/images/product.png'}
                              alt={product.name}
                              className="product-card-image"
                              onError={(e) => {
                                e.target.src = 'https://eposgestofinal.com/site/images/product.png';
                              }}
                            />
                            <div className="product-card-name">{product.name}</div>
                          </div>
                          <div className="product-card-status">
                            {product.inativo === 't' ? (
                              <span className="badge badge-danger">INATIVO</span>
                            ) : (
                              <span className="badge badge-success">ATIVO</span>
                            )}
                          </div>
                        </div>
                        <div className="product-card-info">
                          <div className="product-card-row">
                            <span className="product-card-label">Família:</span>
                            <span 
                              className="product-card-value"
                              style={{ 
                                backgroundColor: categoriesMap && categoriesMap[product.idcategoria] 
                                  ? normalizeColor(categoriesMap[product.idcategoria].color)
                                  : '#007bff',
                                color: categoriesMap && categoriesMap[product.idcategoria] 
                                  ? getContrastColor(categoriesMap[product.idcategoria].color)
                                  : '#ffffff',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                display: 'inline-block',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {product.category}
                            </span>
                          </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {product.gas === 't' && <span 
                                    className="product-card-tag product-card-tag-red"
                                  >
                                    Gás
                                  </span>}
                                  {product.fuel === 't' && <span 
                                    className="product-card-tag product-card-tag-blue"
                                  >
                                    Combustível
                                  </span>}
                            </div>
                          <div className="product-card-data-grid">
                            <div className="product-card-data-item">
                              <span className="product-card-label">P.C. S/IVA:</span>
                              <span className="product-card-value">{product.costprice ? formatPT(product.costprice) : '-'}</span>
                            </div>
                            <div className="product-card-data-item">
                              <span className="product-card-label">P.C. C/IVA:</span>
                              <span className="product-card-value">{product.costprice_iva ? formatPT(product.costprice_iva) : '-'}</span>
                            </div>
                            <div className="product-card-data-item">
                              <span className="product-card-label">PVP s/IVA:</span>
                              <span className="product-card-value">{product.price ? formatPT(product.price) : '-'}</span>
                            </div>
                            <div className="product-card-data-item">
                              <span className="product-card-label">PVP c/IVA:</span>
                              <span className="product-card-value">{product.price_iva ? formatPT(product.price_iva) : '-'}</span>
                            </div>
                            <div className="product-card-data-item">
                              <span className="product-card-label">Margem s/IVA:</span>
                              <span className="product-card-value">{product.profit ? formatPT(product.profit) : '-'}</span>
                            </div>
                            <div className="product-card-data-item">
                              <span className="product-card-label">Margem (%):</span>
                              <span className="product-card-value">{product.profitper ? formatPT(product.profitper) : '-'}</span>
                            </div>
                            <div className="product-card-data-item">
                              <span className="product-card-label">IVA (%):</span>
                              <span className="product-card-value">{product.iva || '-'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="product-card-actions">
                          {product.composed === 't' && (
                            <div
                              className="product-action-btn"
                              onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}/composition`)}
                            >
                              <FaClipboardList />
                              <span>F. Tec.</span>
                            </div>
                          )}
                          <div
                            className="product-action-btn"
                            onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}/stocks`)}
                          >
                            <FaBoxes />
                            <span>Stock</span>
                          </div>
                          <div
                            className="product-action-btn"
                            onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}/prices`)}
                          >
                            <MdEuro />
                            <span>Preços</span>
                          </div>
                          <div
                            className="product-action-btn"
                            onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}`)}
                          >
                            <FaEdit />
                            <span>Editar</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Vista Desktop - Tabela */}
              <div className="products-grid-desktop">
                {table.loading ? (
                  <div style={{ textAlign: "center", padding: "40px" }}>
                    <p>A carregar produtos...</p>
                  </div>
                ) : paginatedProducts.length === 0 ? (
                  <div className="products-empty-state">
                    <p>Nenhum produto encontrado.</p>
                  </div>
                ) : (
                  <>
                    <div className="products-table-desktop">
                      <div className="products-table-header">
                        <div className="products-table-col-product-merged">
                          <span className="products-table-header-label">PRODUTO</span>
                        </div>
                        <div className="products-table-col-price">
                          <span className="products-table-header-label">P.C. S/IVA:</span>
                        </div>
                        <div className="products-table-col-price">
                          <span className="products-table-header-label">P.C. C/IVA:</span>
                        </div>
                        <div className="products-table-col-price">
                          <span className="products-table-header-label">PVP s/IVA</span>
                        </div>
                        <div className="products-table-col-price">
                          <span className="products-table-header-label">PVP c/IVA</span>
                        </div>
                        <div className="products-table-col-price">
                          <span className="products-table-header-label">Margem s/IVA</span>
                        </div>
                        <div className="products-table-col-price">
                          <span className="products-table-header-label">Margem (%)</span>
                        </div>
                        <div className="products-table-col-price">
                          <span className="products-table-header-label">IVA (%)</span>
                        </div>
                        <div className="products-table-col-actions">
                          <span className="products-table-header-label">AÇÕES</span>
                        </div>
                      </div>
                      {paginatedProducts.map((product) => (
                        <div key={product.idproduct} className="products-table-row">
                          <div className="products-table-col-image">
                            {product.inativo === 't' ? (
                              <span className="badge badge-danger">INATIVO</span>
                            ) : (
                              <span className="badge badge-success">ATIVO</span>
                            )}
                          </div>
                          <div className="products-table-col-name">
                            <div className="product-row-name-container">
                              <img
                                src={product.image ? `https://eposgestofinal.com/${product.image}` : 'https://eposgestofinal.com/site/images/product.png'}
                                alt={product.name}
                                className="product-row-image"
                                onError={(e) => {
                                  e.target.src = 'https://eposgestofinal.com/site/images/product.png';
                                }}
                              />
                              <div className="product-row-name-wrapper">
                                <span className="product-row-name">{product.name}</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                  <span 
                                    className="product-row-family"
                                    style={{ 
                                      backgroundColor: categoriesMap && categoriesMap[product.idcategoria] 
                                        ? normalizeColor(categoriesMap[product.idcategoria].color)
                                        : '#007bff',
                                      color: categoriesMap && categoriesMap[product.idcategoria] 
                                        ? getContrastColor(categoriesMap[product.idcategoria].color)
                                        : '#ffffff',
                                      padding: '2px 6px',
                                      borderRadius: '3px',
                                      display: 'inline-block',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {product.category || '-'}
                                  </span>
                                  {product.gas === 't' && <span 
                                    className="product-row-tag product-row-tag-red"
                                  >
                                    Gás
                                  </span>}
                                  {product.fuel === 't' && <span 
                                    className="product-row-tag product-row-tag-blue"
                                  >
                                    Combustível
                                  </span>}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="products-table-col-price">
                            <span className="product-row-value">{product.costprice ? formatPT(product.costprice) : '-'}</span>
                          </div>
                          <div className="products-table-col-price">
                            <span className="product-row-value">{product.costprice_iva ? formatPT(product.costprice_iva) : '-'}</span>
                          </div>
                          <div className="products-table-col-price">
                            <span className="product-row-value">{product.price ? formatPT(product.price) : '-'}</span>
                          </div>
                          <div className="products-table-col-price">
                            <span className="product-row-value">{product.price_iva ? formatPT(product.price_iva) : '-'}</span>
                          </div>
                          <div className="products-table-col-price">
                            <span className="product-row-value">{product.profit ? formatPT(product.profit) : '-'}</span>
                          </div>
                          <div className="products-table-col-price">
                            <span className="product-row-value">{product.profitper ? formatPT(product.profitper) : '-'}</span>
                          </div>
                          <div className="products-table-col-price">
                            <span className="product-row-value">{product.iva || '-'}</span>
                          </div>
                          <div className="products-table-col-actions">
                            <div className="product-row-actions">
                              {product.composed === 't' && (
                                <button
                                  className="product-action-btn-desktop"
                                  onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}/composition`)}
                                  title="Ficha Técnica"
                                >
                                  <FaClipboardList />
                                  <span>F. Tec.</span>
                                </button>
                              )}
                              <button
                                className="product-action-btn-desktop"
                                onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}/stocks`)}
                                title="Stock"
                              >
                                <FaBoxes />
                                <span>Stock</span>
                              </button>
                              <button
                                className="product-action-btn-desktop"
                                onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}/prices`)}
                                title="Preços"
                              >
                                <MdEuro />
                                <span>Preços</span>
                              </button>
                              <button
                                className="product-action-btn-desktop"
                                onClick={() => history.push(`/products/${encodeURIComponent(product.idproduct)}`)}
                                title="Editar"
                              >
                                <FaEdit />
                                <span>Editar</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

            {/* Paginação */}
            {filteredProducts.length > 0 && (
              <div className="products-pagination">
                <div className="products-pagination-info">
                  <span>
                    Mostrando {startIndex + 1} - {Math.min(endIndex, filteredProducts.length)} de {filteredProducts.length} produtos
                  </span>
                  <div className="products-pagination-items-per-page">
                    <label htmlFor="itemsPerPage">Itens por página:</label>
                    <select
                      id="itemsPerPage"
                      value={itemsPerPage}
                      onChange={this.handleItemsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                <div className="products-pagination-controls">
                  <button
                    className="products-pagination-btn"
                    onClick={() => this.handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </button>
                  <span className="products-pagination-page-info">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    className="products-pagination-btn"
                    onClick={() => this.handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Seguinte
                  </button>
                </div>
              </div>
            )}
            {/* <MDBDataTable
                                data={ { ...table, rows: table.rows
                                    .filter((product) => idcategory.value ? product.idcategoria === idcategory.value : true)
                                    .filter((product) => product.name.toLowerCase().includes(search.toLowerCase()) || product.idproduct.toLowerCase().includes(search.toLowerCase()))
                                    .map((product) => ({
                                        name: product.name,
                                        costprice: product.costprice,
                                        costprice_iva: product.costprice_iva,
                                        price: product.price,
                                        price_iva: product.price_iva,
                                        iva: product.iva,
                                        category: product.category,
                                        edit: product.edit,
                                    }))
                                } }
                                striped
                                bordered
                                searching={false}
                            /> */}
          </div>
        </div>
      </div>
    );
  }
}

export default withCookies(withRouter(Page));
